import {
    ColumnFilter,
    ColumnFiltersState,
    FilterFn,
    getCoreRowModel,
    getExpandedRowModel,
    getFacetedMinMaxValues,
    getFacetedRowModel,
    getFacetedUniqueValues,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    useReactTable
} from '@tanstack/react-table';
import React, { useEffect, useMemo, useState } from 'react';
import { ILxsBillingDocument } from '../../../actions/get-billing-documents.action';
import GenericFilterBox, { DateFilterConfig, FilterConfig, MultiFilterConfig } from '../../../components/filter-box';
import { Pagination as PaginationComponent } from '../../../components/pagination';
import SearchBox from '../../../components/search-box';
import { MULTI_VALUE_SEPARATOR } from '../../../configuration';
import {
    BILLING_DATE_FROM_URL_PARAM,
    BILLING_DATE_TO_URL_PARAM,
    BILLING_TYPE_URL_PARAM,
    LXS_BILLING_DOCUMENTS_TABLE_BILLING_DATE,
    LXS_BILLING_DOCUMENTS_TABLE_BILLING_TYPE,
    LXS_BILLING_DOCUMENTS_TABLE_DOCUMENT_ID
} from '../../../configuration/billing-documents';
import { dateLocaleFormatter, toLocalDate } from '../../../utilities';
import { mapBillingTypeToText } from '../../../utilities/map-billing-type-to-text';
import OrderHistoryTableResult from '../../order-history/components/order-history-table-result';
import { ILxsBillingDocumentsTable } from '../lxs-billing-documents.data';
import BillingDocumentsTableBody from './billing-documents-table-body';
import { getBillingDocumentColumns, hiddenColumns } from './billing-documents-table-columns';
import BillingDocumentsTableHead from './billing-documents-table-head';
import { FILTER_BOX_BILLING_DATE_FILTER, FILTER_BOX_BILLING_TYPE_FILTER } from '../../../configuration/filter-box';
import { calculatePageSizeOptions } from '../../../utilities/calculate-page-size-options';

const BillingDocumentsTable: React.FC<ILxsBillingDocumentsTable> = ({
    CLASS_NAME,
    resources,
    globalFilter,
    localizationState,
    data,
    context,
    setGlobalFilter
}) => {
    const isCollapsible: boolean = false;
    const showSearchBoxFilters: boolean = false;
    const [pageIndex, setPageIndex] = useState(0);
    const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
    const [pagination, setPagination] = useState<{ pageIndex: number; pageSize: number }>({
        pageIndex: pageIndex,
        pageSize: 10
    });
    const [showFilterBox] = useState<boolean>(true);

    useEffect(() => {
        setPageIndex(0);
    }, [globalFilter]);

    useEffect(() => {
        try {
            const url = new URL(window.location.href);
            const fromDate = url.searchParams.get(BILLING_DATE_FROM_URL_PARAM) ?? '';
            const toDate = url.searchParams.get(BILLING_DATE_TO_URL_PARAM) ?? '';
            const filters = [];
            if (fromDate || toDate) {
                filters.push({ id: LXS_BILLING_DOCUMENTS_TABLE_BILLING_DATE, value: [fromDate, toDate] });
            }

            const values = url.searchParams.getAll(BILLING_TYPE_URL_PARAM);
            if (values && values.length > 0) {
                filters.push({
                    id: LXS_BILLING_DOCUMENTS_TABLE_BILLING_TYPE,
                    value: values.join(MULTI_VALUE_SEPARATOR)
                });
            } else {
                setColumnFilters([]);
            }
            setColumnFilters(filters);
        } catch (e) {
            setColumnFilters([]);
        }
    }, []);

    const columns = useMemo(() => getBillingDocumentColumns(resources, localizationState, globalFilter ?? '', data), [
        resources,
        localizationState,
        globalFilter
    ]);

    const defaultSorting = useMemo(
        () => [
            {
                id: LXS_BILLING_DOCUMENTS_TABLE_DOCUMENT_ID,
                desc: true
            }
        ],
        []
    );

    // Filter function to search for all order items based on user input
    const textFilter: FilterFn<ILxsBillingDocument> = (row, _, filterValue) => {
        let highlightRow = false;
        const inMainData = Object.entries(row.original).some(([_, value]) => {
            if (typeof value === 'string') {
                return value.toLowerCase().includes(filterValue.toLowerCase());
            }
            return false;
        });

        if (inMainData) {
            highlightRow = true;
        }

        row.original.Highlight = highlightRow;

        return highlightRow;
    };

    const table = useReactTable({
        data,
        columns,
        sortingFns: {},
        filterFns: {},
        initialState: { sorting: defaultSorting, columnVisibility: hiddenColumns },
        state: {
            globalFilter,
            columnFilters,
            pagination: pagination
        },
        onColumnFiltersChange: setColumnFilters,
        onGlobalFilterChange: setGlobalFilter,
        globalFilterFn: textFilter,
        getCoreRowModel: getCoreRowModel(),
        getExpandedRowModel: getExpandedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        onPaginationChange: setPagination,
        getPaginationRowModel: getPaginationRowModel(),
        getFacetedRowModel: getFacetedRowModel(),
        getFacetedUniqueValues: getFacetedUniqueValues(),
        getFacetedMinMaxValues: getFacetedMinMaxValues()
    });

    const filterConfig = useMemo<FilterConfig[]>(
        () => [
            {
                id: FILTER_BOX_BILLING_DATE_FILTER,
                legend: resources.lxsBillingDocumentsBillingDateTitle,
                type: 'date' as const,
                columnId: LXS_BILLING_DOCUMENTS_TABLE_BILLING_DATE,
                fromParamName: BILLING_DATE_FROM_URL_PARAM,
                toParamName: BILLING_DATE_TO_URL_PARAM
            } as DateFilterConfig,
            {
                id: FILTER_BOX_BILLING_TYPE_FILTER,
                legend: resources.globalFilterBillingTypeLegend,
                type: 'multi' as const,
                columnId: LXS_BILLING_DOCUMENTS_TABLE_BILLING_TYPE,
                paramName: BILLING_TYPE_URL_PARAM,
                mapKeyToText: (key: string) => {
                    return mapBillingTypeToText(data, key);
                }
            } as MultiFilterConfig
        ],
        [resources]
    );

    const removeUrlParameter = (paramNames: string[]) => {
        const currentUrl = new URL(window.location.href);
        paramNames.forEach(paramName => {
            currentUrl.searchParams.delete(paramName);
        });
        window.history.pushState({}, '', currentUrl.toString());
    };

    return (
        <div className='row'>
            <div className={`col-12 ${CLASS_NAME}__container`}>
                <div className={`col ${showFilterBox ? `${CLASS_NAME}__search--small` : `${CLASS_NAME}__search--large`}`}>
                    <SearchBox
                        globalFilter={globalFilter ?? ''}
                        resources={resources}
                        setGlobalFilter={setGlobalFilter}
                        currentView={undefined}
                        activeButton={undefined}
                        showSearchBoxFilters={showSearchBoxFilters}
                        setCurrentView={undefined}
                        handleTimeFilterClick={undefined}
                        handleOpenFilterClick={undefined}
                    />
                </div>
                <div className={`col ${showFilterBox ? `${CLASS_NAME}__table--small` : `${CLASS_NAME}__table--large`}`}>
                    <div className={`${CLASS_NAME}__table-head`}>
                        <h3 className={`${CLASS_NAME}__total-documents`}>
                            {`${table.getFilteredRowModel().rows.length} ${resources.lxsBillingDocumentsTableHeader}`}
                        </h3>
                        {columnFilters?.length ? (
                            <div className={`${CLASS_NAME}__filter-section`}>
                                {columnFilters.map((filter: ColumnFilter, index: number) => (
                                    <div className={`${CLASS_NAME}__active-filter`} key={index}>
                                        {filter.id === LXS_BILLING_DOCUMENTS_TABLE_BILLING_DATE ? (
                                            (() => {
                                                const [fromDate, toDate] = filter.value as string[];
                                                if (!fromDate && !toDate) {
                                                    return null;
                                                }

                                                const formattedFrom = fromDate
                                                    ? dateLocaleFormatter(localizationState).formatDate(toLocalDate(fromDate))
                                                    : '';
                                                const formattedTo = toDate
                                                    ? dateLocaleFormatter(localizationState).formatDate(toLocalDate(toDate))
                                                    : '';

                                                const displayText =
                                                    fromDate && toDate
                                                        ? `${formattedFrom} – ${formattedTo}`
                                                        : fromDate
                                                        ? `${resources.globalFilterFromLabel} ${formattedFrom}`
                                                        : `${resources.globalFilterToLabel} ${formattedTo}`;

                                                return (
                                                    <div className={`${CLASS_NAME}__active-filter-container`}>
                                                        <div className={`${CLASS_NAME}__active-filter-item`}>
                                                            {displayText}
                                                            <button
                                                                onClick={() => {
                                                                    removeUrlParameter([
                                                                        BILLING_DATE_FROM_URL_PARAM,
                                                                        BILLING_DATE_TO_URL_PARAM
                                                                    ]);
                                                                    setColumnFilters(
                                                                        columnFilters.filter(
                                                                            columnFilter =>
                                                                                columnFilter.id !== LXS_BILLING_DOCUMENTS_TABLE_BILLING_DATE
                                                                        )
                                                                    );
                                                                }}
                                                                className={`${CLASS_NAME}__active-filter-item-remove-button`}
                                                            ></button>
                                                        </div>
                                                    </div>
                                                );
                                            })()
                                        ) : (
                                            <div className={`${CLASS_NAME}__active-filter-container`}>
                                                {(filter.value as string)
                                                    .split(MULTI_VALUE_SEPARATOR)
                                                    .map((value: string, index: number) => (
                                                        <div className={`${CLASS_NAME}__active-filter-item`} key={index}>
                                                            {mapBillingTypeToText(data, value)}
                                                            <button
                                                                onClick={() => {
                                                                    const currentValues = (filter.value as string).split(
                                                                        MULTI_VALUE_SEPARATOR
                                                                    );
                                                                    const filteredValues = currentValues.filter(
                                                                        currentValue => currentValue !== value
                                                                    );

                                                                    const url = new URL(window.location.href);
                                                                    url.searchParams.delete(BILLING_TYPE_URL_PARAM);
                                                                    filteredValues.forEach(value =>
                                                                        url.searchParams.append(BILLING_TYPE_URL_PARAM, value)
                                                                    );
                                                                    window.history.pushState({}, '', url.toString());

                                                                    if (filteredValues?.length) {
                                                                        setColumnFilters([
                                                                            ...columnFilters.filter(
                                                                                columnFilter => columnFilter.id !== filter.id
                                                                            ),
                                                                            {
                                                                                id: filter.id,
                                                                                value: filteredValues.join(MULTI_VALUE_SEPARATOR)
                                                                            }
                                                                        ]);
                                                                    } else {
                                                                        setColumnFilters(
                                                                            columnFilters.filter(
                                                                                columnFilter => columnFilter.id !== filter.id
                                                                            )
                                                                        );
                                                                    }
                                                                }}
                                                                className={`${CLASS_NAME}__active-filter-item-remove-button`}
                                                            ></button>
                                                        </div>
                                                    ))}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        ) : (
                            ''
                        )}
                    </div>

                    <div className='table-responsive'>
                        <table className={`table ${CLASS_NAME}__table`}>
                            <BillingDocumentsTableHead table={table} resources={resources} context={context} />
                            <BillingDocumentsTableBody table={table} CLASS_NAME={CLASS_NAME} resources={resources} context={context} />
                        </table>
                    </div>

                    <div className={`${CLASS_NAME}__table-bottom`}>
                        <PaginationComponent
                            pageIndex={table.getState().pagination.pageIndex}
                            resources={resources}
                            table={table}
                            pageSizeOptions={calculatePageSizeOptions(table.getFilteredRowModel().rows.length)}
                            componentName={OrderHistoryTableResult.name}
                        />
                    </div>
                </div>

                <div
                    className={`col-12 col-lg-3 order-1 order-lg-2 ${CLASS_NAME}__filter-box-wrapper ${
                        showFilterBox ? `${CLASS_NAME}__filter-box-wrapper--show` : `${CLASS_NAME}__filter-box-wrapper--hide`
                    }`}
                >
                    <GenericFilterBox
                        table={table}
                        resources={resources}
                        context={context}
                        localizationState={localizationState}
                        columnFilters={columnFilters}
                        filters={filterConfig}
                        isCollapsible={isCollapsible}
                        setColumnFilters={setColumnFilters}
                    />
                </div>
            </div>
        </div>
    );
};

export default BillingDocumentsTable;
